// Patient identifiers: CR ID, national ID, birth certificate, passport and the rest.
//
// An identifier is how two records of the same person are recognised as one, so the same
// number typed twice has to compare equal: `1234 5678`, `1234-5678` and ` 12345678 ` are
// one number. `normaliseIdentifier` produces the form stored in
// `patient_identifiers.value_normalised` and used for every exact match; the value as
// typed is kept alongside it for display.
//
// Deliberately absent: per-system format rules (length, allowed characters, check digits).
// We have no authoritative specification for any of them yet, and rejecting a real
// document number because of a guessed rule would force a receptionist to register a
// person without it. TODO(verify-spec): add per-system validation when the Client
// Registry and the issuing authorities' formats are documented in docs/integrations/.

import type { Result } from './result.ts';
import { err, ok } from './result.ts';

export const IDENTIFIER_SYSTEMS = [
  'cr_id',
  'national_id',
  'birth_certificate',
  'passport',
  'alien_id',
  'refugee_id',
  'legacy_patient_number',
] as const;

export type IdentifierSystem = (typeof IDENTIFIER_SYSTEMS)[number];

/**
 * Systems where a number belongs to one person in the tenant: a second active record
 * with the same number is refused, not offered as a duplicate candidate (plan §12,
 * decision 7). Legacy numbers are left out because two facilities' old systems can have
 * issued the same number to different people.
 */
export const TENANT_UNIQUE_IDENTIFIER_SYSTEMS: readonly IdentifierSystem[] = [
  'cr_id',
  'national_id',
  'birth_certificate',
  'passport',
  'alien_id',
  'refugee_id',
];

export function isTenantUniqueIdentifierSystem(system: IdentifierSystem): boolean {
  return TENANT_UNIQUE_IDENTIFIER_SYSTEMS.includes(system);
}

/** Only a passport carries an issuing country; for every other system it must be absent. */
export function identifierNeedsIssuingCountry(system: IdentifierSystem): boolean {
  return system === 'passport';
}

/** Longer than any document number we know of, short enough to stop pasted paragraphs. */
export const IDENTIFIER_MAX_LENGTH = 64;

export type IdentifierErrorCode =
  'identifier.empty' | 'identifier.too_long' | 'identifier.invalid_characters';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const IDENTIFIER_ERRORS: Record<IdentifierErrorCode, string> = {
  'identifier.empty': 'No number was entered',
  'identifier.too_long': `An identifier is at most ${String(IDENTIFIER_MAX_LENGTH)} characters`,
  'identifier.invalid_characters': 'An identifier cannot contain control characters',
};

/** Spaces (of any kind) and dashes (hyphen, en dash, em dash, minus sign). */
const IGNORED = /[\s\u2010-\u2015\u2212-]/g;
// eslint-disable-next-line no-control-regex -- control characters are exactly what this rejects
const CONTROL = /[\u0000-\u001f\u007f-\u009f]/;

/**
 * The stored matching form: trimmed, upper case, spaces and dashes removed. The system is
 * a parameter so per-system rules can be added without changing every caller; today every
 * system is normalised the same way.
 */
export function normaliseIdentifier(
  _system: IdentifierSystem,
  value: string,
): Result<string, IdentifierErrorCode> {
  if (CONTROL.test(value.replace(/[\t\n\r]/g, ' '))) return err('identifier.invalid_characters');
  const normalised = value.replace(IGNORED, '').toUpperCase();
  if (normalised === '') return err('identifier.empty');
  if (normalised.length > IDENTIFIER_MAX_LENGTH) return err('identifier.too_long');
  return ok(normalised);
}

/**
 * Whether a search query could be an identifier or a patient number, and its normalised
 * form if so. A query with a digit and nothing but letters, digits and a few separators
 * is looked up exactly across all identifier systems and patient numbers; anything else
 * is treated as a name. Names with digits in them are rare enough that a name query
 * which also matches this simply gets both kinds of search.
 */
export function identifierLooksLike(query: string): string | null {
  const normalised = query.replace(IGNORED, '').toUpperCase();
  if (normalised === '' || normalised.length > IDENTIFIER_MAX_LENGTH) return null;
  if (!/\d/.test(normalised)) return null;
  if (!/^[\p{L}\p{N}/.]+$/u.test(normalised)) return null;
  return normalised;
}
